import React from "react";
import {
    Card,
    CardContent,
    Typography,
    CircularProgress,
    Box,
    Chip,
    Skeleton,
} from "@mui/material";

interface StatCardProps {
    title: string;
    value?: string | number;
    subtitle?: string;
    currentYearValue?: number; // Current year value prop
    previousYearValue?: number; // Previous year value prop
    loading?: boolean;
    children?: React.ReactNode;
}

const StatCard: React.FC<StatCardProps> = ({
    title,
    value,
    subtitle,
    currentYearValue,
    previousYearValue,
    loading = false,
    children,
}) => {
    // Calculate the percentage difference between the current and previous year values
    const percentageDifference =
        currentYearValue && previousYearValue
            ? ((currentYearValue - previousYearValue) / previousYearValue) * 100
            : null;

    return (
        <Card elevation={0}>
            <CardContent>
                <Box
                    className="w-full flex-col justify-start mb-1 items-start text-left"
                    sx={{ display: "flex" }}
                >
                    <Typography fontWeight={500}>{title}</Typography>

                    {(value !== undefined || subtitle) && (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "baseline",
                                gap: 1, // Gap between value and chip
                            }}
                        >
                            {loading ||
                            value === "0.0k €" ||
                            value === "0.0k hL" ? (
                                // Show Skeleton for the value if loading or value is undefined
                                <Skeleton
                                    variant="text"
                                    width={80}
                                    height={40}
                                />
                            ) : (
                                <Typography variant="h6" fontWeight={500}>
                                    {value}
                                </Typography>
                            )}

                            {loading ||
                            !currentYearValue ||
                            !previousYearValue ? (
                                <></>
                            ) : (
                                <Chip
                                    label={`${
                                        percentageDifference >= 0 ? "+" : ""
                                    }${percentageDifference.toFixed(0)}%`}
                                    sx={{
                                        backgroundColor:
                                            percentageDifference >= 0
                                                ? "#C3FF36"
                                                : "#FF365E",
                                        color:
                                            percentageDifference >= 0
                                                ? "black"
                                                : "white",
                                    }}
                                    size="small"
                                />
                            )}

                            {subtitle && (
                                <Typography
                                    variant="subtitle1"
                                    fontWeight={400}
                                    color="text.secondary"
                                >
                                    {subtitle}
                                </Typography>
                            )}
                        </Box>
                    )}
                </Box>

                {loading ? <CircularProgress /> : children}
            </CardContent>
        </Card>
    );
};

export default StatCard;
